import { supabase } from '@/lib/supabase';
import { parseAikenFormat } from '@/utils/aiken-parser';
import { normalizeDifficulty } from '@/types/quiz';

export interface ImportResult {
  totalProcessed: number;
  importedCount: number;
  errors: Array<{ line: number; message: string }>;
  rejectionReasons: Record<string, number>;
}

/**
 * Categorizes an Aiken question's difficulty based on content analysis
 * Uses multiple factors: question length, option length, newlines, and word count
 */
export function categorizeAikenQuestionDifficulty(question: {
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  option_e?: string;
}): "easy" | "medium" | "hard" {
  // Check if question has newlines (indicator of hard difficulty)
  if (question.question_text.includes('\n')) {
    return "hard";
  }
  
  // Calculate content length metrics
  const questionLength = question.question_text.length;
  const optionsText = [
    question.option_a, 
    question.option_b, 
    question.option_c, 
    question.option_d,
    question.option_e || ""
  ].filter(Boolean); // Filter out empty options
  
  // Check option word count (medium difficulty indicator)
  const optionWordCounts = optionsText.map(option => {
    // Count words by splitting on whitespace
    const words = option.trim().split(/\s+/);
    return words.length;
  });
  
  // Calculate total options length
  const optionsLength = optionsText.reduce((sum, option) => sum + option.length, 0);
  
  // Check for mathematical or technical content (indicators of increased difficulty)
  const hasMathContent = /[+\-*/^=(){}[\]|<>]/.test(question.question_text) || 
                         optionsText.some(opt => /[+\-*/^=(){}[\]|<>]/.test(opt));
                         
  const hasComplexTerms = /\b(therefore|furthermore|consequently|nevertheless|accordingly)\b/i.test(question.question_text);
  
  // Apply categorization rules combining multiple approaches
  
  // Hard difficulty criteria
  if (
    questionLength > 120 || 
    optionsLength > 250 ||
    optionsText.some(option => option.length > 70) ||
    (hasMathContent && hasComplexTerms) ||
    question.question_text.split('.').length > 3 // Multiple sentences indicate complexity
  ) {
    return "hard";
  }
  
  // Medium difficulty criteria
  if (
    questionLength > 80 || 
    optionsLength > 150 ||
    optionWordCounts.some(count => count > 6) ||
    optionsText.some(option => option.length > 50) ||
    hasMathContent ||
    hasComplexTerms
  ) {
    return "medium";
  }
  
  // Default to easy difficulty
  return "easy";
}

export const importQuizAikenQuestions = async (
  file: File,
  subject: string
): Promise<ImportResult> => {
  try {
    // Read file content
    const content = await file.text();
    
    // Parse the Aiken format content
    const examDetails = {
      examName: 'Quiz',
      subject: subject,
      setName: 'Aiken Import'
    };
    
    const parseResult = parseAikenFormat(content, examDetails);
    
    const result: ImportResult = {
      totalProcessed: parseResult.stats.totalLines,
      importedCount: 0,
      errors: [],
      rejectionReasons: parseResult.stats.skippedReasons
    };
    
    // Convert skipped rows to error format
    result.errors = parseResult.stats.skippedRows.map(row => ({
      line: row.rowIndex,
      message: row.reason
    }));
    
    if (parseResult.questions.length === 0) {
      result.errors.push({ 
        line: 0, 
        message: 'No valid questions found in the file' 
      });
      return result;
    }
    
    console.log(`Parsed ${parseResult.questions.length} questions`);
    
    // Convert parsed questions to the format required by the questions table
    const questionsToInsert = parseResult.questions.map(question => {
      // Map correct option letter (A, B, C, D) to the corresponding option text
      const correctLetter = question.correct_option.toUpperCase();
      let correctAnswer = '';
      
      switch (correctLetter) {
        case 'A': correctAnswer = question.option_a; break;
        case 'B': correctAnswer = question.option_b; break;
        case 'C': correctAnswer = question.option_c; break;
        case 'D': correctAnswer = question.option_d; break;
        case 'E': correctAnswer = question.option_e || ''; break;
      }
      
      // Create question data object for difficulty categorization
      const questionData = {
        question_text: question.question_text,
        option_a: question.option_a,
        option_b: question.option_b,
        option_c: question.option_c,
        option_d: question.option_d,
        option_e: question.option_e
      };
      
      // Automatically categorize difficulty
      const calculatedDifficulty = categorizeAikenQuestionDifficulty(questionData);
      
      // Transform to questions table format
      return {
        Category: question.subject || subject,
        Question: question.question_text,
        "Option A": question.option_a,
        "Option B": question.option_b,
        "Option C": question.option_c,
        "Option D": question.option_d,
        "Correct Answer": correctAnswer,
        "Difficulty": normalizeDifficulty(calculatedDifficulty), // Use auto-categorized difficulty
        "Explanation": null, // No explanation in Aiken format
        source: 'aiken_import',
        status: 'verified',
        ai_generated: false,
        question_type: 'mcq'
      };
    });
    
    // Insert questions in batches to avoid timeout
    const BATCH_SIZE = 20;
    const batches = [];
    
    for (let i = 0; i < questionsToInsert.length; i += BATCH_SIZE) {
      batches.push(questionsToInsert.slice(i, i + BATCH_SIZE));
    }
    
    let inserted = 0;
    
    for (const batch of batches) {
      try {
        const { data, error } = await supabase
          .from('questions')
          .insert(batch)
          .select('ID');
        
        if (error) {
          console.error('Error inserting batch:', error);
          result.errors.push({
            line: 0,
            message: `Database error: ${error.message}`
          });
        } else {
          inserted += (data?.length || 0);
        }
      } catch (batchError) {
        console.error('Exception when inserting batch:', batchError);
        result.errors.push({
          line: 0,
          message: `Exception: ${batchError instanceof Error ? batchError.message : String(batchError)}`
        });
      }
    }
    
    result.importedCount = inserted;
    console.log(`Successfully imported ${inserted} questions with adaptive difficulty into the questions table`);
    
    return result;
  } catch (error) {
    console.error('Error in importQuizAikenQuestions:', error);
    return {
      totalProcessed: 0,
      importedCount: 0,
      errors: [{
        line: 0,
        message: `Import failed: ${error instanceof Error ? error.message : String(error)}`
      }],
      rejectionReasons: {}
    };
  }
};

export const deleteExistingQuizAikenQuestions = async (subject: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('questions')
      .delete()
      .eq('Category', subject)
      .eq('source', 'aiken_import');
    
    if (error) {
      console.error('Error deleting existing questions:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Exception when deleting existing questions:', error);
    return false;
  }
};

/**
 * Fetch questions available for multiplication with variation counts
 */
export const fetchQuestionsForMultiplication = async (
  category: string | null = null,
  limit: number = 100
) => {
  try {
    const { data, error } = await supabase.rpc('get_questions_for_multiplication', {
      category_filter: category,
      limit_count: limit
    });

    if (error) {
      console.error('Error fetching questions for multiplication:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Exception in fetchQuestionsForMultiplication:', error);
    throw error;
  }
};

/**
 * Generate variations of a question using the PYQ multiplier edge function
 */
export const generateQuestionVariations = async (
  questionId: number,
  question: string,
  options: string[],
  correctAnswer: string,
  numVariations: number = 3,
  examName: string = "AI Generated Mock Test",
  sectionName: string = "General",
  category: string = "General",
  difficulty: string = "medium"
) => {
  try {
    const response = await supabase.functions.invoke('pyq-multiplier', {
      body: {
        questionId,
        question,
        options,
        correctAnswer,
        numVariations,
        examName,
        sectionName,
        category,
        difficulty
      }
    });

    if (response.error) {
      console.error('Error invoking pyq-multiplier function:', response.error);
      throw new Error(response.error.message);
    }

    return response.data;
  } catch (error) {
    console.error('Exception in generateQuestionVariations:', error);
    throw error;
  }
};

/**
 * Fetch existing question variations for a specific original question
 */
export const fetchQuestionVariations = async (originalQuestionId: number) => {
  try {
    const { data, error } = await supabase
      .from('mock_test_questions')
      .select('*')
      .filter('metadata->original_question_id', 'eq', originalQuestionId.toString())
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching question variations:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Exception in fetchQuestionVariations:', error);
    throw error;
  }
};
